import { useMemo } from 'react';
import type { ModuleCategory } from '@eskoolhub/types';
import { Switch } from './ui';

export interface BoardModule {
  key: string;
  name: string;
  description: string;
  category: ModuleCategory;
  isCore: boolean;
  requires: string[];
  /** True when the currently selected plan includes this module by default. */
  planDefault: boolean;
}

const CATEGORY_LABELS: Record<ModuleCategory, string> = {
  CORE: 'Core · always on',
  COMMUNICATION: 'Communication',
  ACADEMICS: 'Academics',
  FINANCE: 'Fees & finance',
  OPERATIONS: 'Operations',
  PLATFORM: 'Platform',
};

const CATEGORY_ORDER: ModuleCategory[] = [
  'CORE',
  'COMMUNICATION',
  'ACADEMICS',
  'FINANCE',
  'OPERATIONS',
  'PLATFORM',
];

/**
 * The feature switchboard. Enabling a module pulls in whatever it requires;
 * disabling one turns off everything that depends on it — so the selection the
 * Super Admin sees is always one the API will accept.
 */
export function ModuleBoard({
  modules,
  selected,
  onChange,
  readOnly,
}: {
  modules: BoardModule[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  readOnly?: boolean;
}) {
  const byCategory = useMemo(() => {
    const map = new Map<ModuleCategory, BoardModule[]>();
    for (const module of modules) {
      const list = map.get(module.category) ?? [];
      list.push(module);
      map.set(module.category, list);
    }
    return map;
  }, [modules]);

  const dependents = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const module of modules) {
      for (const requirement of module.requires) {
        map.set(requirement, [...(map.get(requirement) ?? []), module.key]);
      }
    }
    return map;
  }, [modules]);

  function toggle(module: BoardModule, next: boolean) {
    if (module.isCore) return;
    const updated = new Set(selected);

    if (next) {
      updated.add(module.key);
      // Pull in dependencies, and their dependencies.
      const queue = [...module.requires];
      while (queue.length > 0) {
        const key = queue.pop()!;
        if (updated.has(key)) continue;
        updated.add(key);
        const dependency = modules.find((m) => m.key === key);
        if (dependency) queue.push(...dependency.requires);
      }
    } else {
      updated.delete(module.key);
      // Cascade: anything that needs this module goes off with it.
      const queue = [...(dependents.get(module.key) ?? [])];
      while (queue.length > 0) {
        const key = queue.pop()!;
        if (!updated.has(key)) continue;
        updated.delete(key);
        queue.push(...(dependents.get(key) ?? []));
      }
    }

    onChange(updated);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {CATEGORY_ORDER.filter((category) => byCategory.has(category)).map((category) => (
        <section key={category} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div className="label">{CATEGORY_LABELS[category]}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
            {byCategory.get(category)!.map((module) => {
              const isOn = module.isCore || selected.has(module.key);
              const isOverride = !module.isCore && isOn !== module.planDefault;

              return (
                <div key={module.key} className={isOn ? 'module-row module-row--on' : 'module-row'}>
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <div className="module-row__name">
                      {module.name}
                      {module.isCore ? <span className="tag tag--core">Core</span> : null}
                      {isOverride ? (
                        <span className="tag tag--manual">{isOn ? 'Added' : 'Removed'}</span>
                      ) : null}
                    </div>
                    <div className="module-row__desc">{module.description}</div>
                    {module.requires.length > 0 ? (
                      <div className="module-row__desc" style={{ opacity: 0.75 }}>
                        Needs: {module.requires.map((r) => modules.find((m) => m.key === r)?.name ?? r).join(', ')}
                      </div>
                    ) : null}
                  </div>
                  <Switch
                    checked={isOn}
                    disabled={module.isCore || readOnly}
                    onChange={(next) => toggle(module, next)}
                    label={`${module.name} for this school`}
                  />
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

/** Summary line: "12 on · 2 beyond plan · 1 removed". */
export function moduleSummary(modules: BoardModule[], selected: Set<string>): string {
  const on = modules.filter((m) => m.isCore || selected.has(m.key));
  const added = modules.filter((m) => !m.isCore && selected.has(m.key) && !m.planDefault);
  const removed = modules.filter((m) => !m.isCore && !selected.has(m.key) && m.planDefault);

  const parts = [`${on.length} of ${modules.length} on`];
  if (added.length > 0) parts.push(`${added.length} beyond plan`);
  if (removed.length > 0) parts.push(`${removed.length} removed`);
  return parts.join(' · ');
}
